/**
 * Browser detection utilities.
 */

import type { BrowserName, BrowserInfo } from "./types";
import { debugLogger } from "./debugLogger";
import { _detectOSInternal } from "./osDetection";
import { getFriendlyOSName } from "./userFriendlyNames";

/**
 * Extracts version number from a version string.
 * :param versionString: The version string to parse.
 * :return: The major version number or null if not found.
 */
function extractVersion(versionString: string | null): string | null {
  if (!versionString) return null;
  const match = versionString.match(/(\d+(?:\.\d+)?)/);
  return match ? match[1] : null;
}

/**
 * Extracts browser version from the user agent string.
 * :param browserName: The detected browser name.
 * :return: The browser version string or null if not found.
 */
function extractBrowserVersion(browserName: BrowserName): string | null {
  const userAgent = navigator.userAgent;
  let match: RegExpMatchArray | null = null;

  switch (browserName) {
    case "edge":
      // Edge Chromium: Edg/120.0.0.0 or Edge/120.0.0.0
      match = userAgent.match(/Edg(e)?\/([\d.]+)/);
      break;
    case "opera":
      // Opera: OPR/106.0.0.0 or Version/106.0.0.0
      match = userAgent.match(/(?:OPR|Version)\/([\d.]+)/);
      break;
    case "chrome":
      // Chrome: Chrome/120.0.0.0
      match = userAgent.match(/Chrome\/([\d.]+)/);
      break;
    case "firefox":
      // Firefox: Firefox/121.0
      match = userAgent.match(/Firefox\/([\d.]+)/);
      break;
    case "safari":
      // Safari: Version/17.2.1
      match = userAgent.match(/Version\/([\d.]+)/);
      break;
    case "yandex":
      // Yandex: YaBrowser/23.9.0.0
      match = userAgent.match(/YaBrowser\/([\d.]+)/);
      break;
    case "brave":
      // Brave uses Chrome version
      match = userAgent.match(/Chrome\/([\d.]+)/);
      break;
    default:
      return null;
  }

  return match ? match[1] : null;
}

/**
 * Pure browser name detection logic without logging.
 * :return: The detected browser name.
 */
function _detectBrowserNameInternal(): BrowserName {
  const userAgent = navigator.userAgent;

  // Check for Edge (must check before Chrome, as Edge contains Chrome in user agent)
  if (/Edg(e)?\//.test(userAgent)) {
    return "edge";
  }

  // Check for Opera (must check before Chrome, as Opera contains Chrome in user agent)
  if (/OPR\//.test(userAgent) || /Opera/.test(userAgent)) {
    return "opera";
  }

  // Check for Brave (must check before Chrome, as Brave contains Chrome in user agent)
  // Brave doesn't have a unique identifier, but we can check for Brave-specific properties
  const hasBraveAPI =
    (navigator as any).brave &&
    typeof (navigator as any).brave.isBrave === "function";
  if (hasBraveAPI) {
    return "brave";
  }

  // Check for Yandex
  if (/YaBrowser/.test(userAgent)) {
    return "yandex";
  }

  // Check for Chrome (must check after Edge, Opera, Brave, Yandex)
  if (/Chrome\//.test(userAgent) && !/Chromium/.test(userAgent)) {
    return "chrome";
  }

  // Check for Firefox
  if (/Firefox\//.test(userAgent)) {
    return "firefox";
  }

  // Check for Safari (must check after Chrome, as Safari contains Chrome in user agent)
  if (
    /Safari\//.test(userAgent) &&
    !/Chrome/.test(userAgent) &&
    !/Chromium/.test(userAgent)
  ) {
    return "safari";
  }

  return "unknown";
}

/**
 * Detects the browser name from the user agent string.
 * :return: The detected browser name.
 */
export function detectBrowserName(): BrowserName {
  const userAgent = navigator.userAgent;

  debugLogger.log("Detecting browser name...", { userAgent });

  // Detect OS to include in browser detection logs (use internal to avoid double logging)
  const os = _detectOSInternal();
  const friendlyOS = getFriendlyOSName(os);

  const result = _detectBrowserNameInternal();
  const version = extractBrowserVersion(result);

  debugLogger.logDetection("Browser", result, {
    userAgent,
    version: version || "unknown",
    os,
    osFriendly: friendlyOS,
  });

  return result;
}

/**
 * Gets detailed browser information including name and version.
 * :return: Browser information object with name, version, and user agent.
 */
export function detectBrowser(): BrowserInfo {
  // Use internal function to avoid double logging (detectBrowserName already logs)
  const browserName = _detectBrowserNameInternal();
  const fullVersion = extractBrowserVersion(browserName);
  const version = extractVersion(fullVersion);

  const browserInfo = {
    name: browserName,
    version,
    fullVersion,
    userAgent: navigator.userAgent,
  };

  // Log with OS information for complete context
  const os = _detectOSInternal();
  const friendlyOS = getFriendlyOSName(os);

  debugLogger.logDetection("Browser", browserName, {
    version,
    fullVersion,
    userAgent: navigator.userAgent,
    os,
    osFriendly: friendlyOS,
  });

  return browserInfo;
}

/**
 * Detects if the current browser is Google Chrome.
 * :return: True if running on Chrome, false otherwise.
 */
export function isChrome(): boolean {
  return detectBrowserName() === "chrome";
}

/**
 * Detects if the current browser is Microsoft Edge.
 * :return: True if running on Edge, false otherwise.
 */
export function isEdgeBrowser(): boolean {
  return detectBrowserName() === "edge";
}

/**
 * Detects if the current browser is Mozilla Firefox.
 * :return: True if running on Firefox, false otherwise.
 */
export function isFirefox(): boolean {
  return detectBrowserName() === "firefox";
}

/**
 * Detects if the current browser is Safari.
 * :return: True if running on Safari, false otherwise.
 */
export function isSafari(): boolean {
  return detectBrowserName() === "safari";
}

/**
 * Detects if the current browser is Opera.
 * :return: True if running on Opera, false otherwise.
 */
export function isOpera(): boolean {
  return detectBrowserName() === "opera";
}

/**
 * Detects if the current browser is Brave.
 * :return: True if running on Brave, false otherwise.
 */
export function isBrave(): boolean {
  return detectBrowserName() === "brave";
}

/**
 * Detects if the current browser is Yandex.
 * :return: True if running on Yandex, false otherwise.
 */
export function isYandex(): boolean {
  return detectBrowserName() === "yandex";
}

/**
 * Gets the browser version string.
 * :return: The browser version or null if not available.
 */
export function getBrowserVersion(): string | null {
  const browserInfo = detectBrowser();
  return browserInfo.version;
}

/**
 * Gets the full browser version string (including minor versions).
 * :return: The full browser version or null if not available.
 */
export function getBrowserFullVersion(): string | null {
  const browserInfo = detectBrowser();
  return browserInfo.fullVersion;
}
